/* ---------------------------------------------------------------------------
   Pawfect Picture — studio, Shopify theme build.

   COMPOSITING IS BACK, and the reason it is safe this time is worth stating
   plainly, because it was removed for a good reason and the old warning is
   still true of the old design.

   The Worker used to send a finished poster. Now, when spec.layer === "cutout",
   it sends the ANIMAL ALONE on transparency — and that layer already has the
   watermark burnt across the animal's own pixels before it was ever published.
   So the browser can draw the background, scale and place the animal and set
   the name, and the thing it holds is still worthless to anyone who saves it.
   The clean matte never leaves the private bucket.

   What this buys: the customer changes the colour, the name and the font and
   sees it immediately, with no second generation and no second 14 cents. It
   also fixes framing. Measured across 22 real previews the animal's own top
   ranged from 8.7% to 30.2% of the poster; no prompt or reference position we
   tried moved that. Here it is arithmetic — measure the alpha, scale to a fixed
   height, place at a fixed top — so every poster matches.

   If the Worker cannot cut the animal out it sends a finished poster as before
   and everything below falls back to simply displaying it.

   Style options come from the Worker's /api/styles. Variants come from Liquid.
   Frame mockups come from section blocks. Nothing about the product catalogue
   is hardcoded here, so deleting a variant or adding a frame in Shopify shows
   up on the page with no code change.

   The mockup carousel works the way the competitor's does: the portrait is
   composited once to an off-screen canvas, then that single image is placed
   into the cutout window of each frame photograph. Choosing a frame on the
   right switches the carousel on the left to the matching mockup.

   PHOTO COUNT IS THE STYLE'S DECISION, not this file's. maxPhotos arrives in
   the /api/styles payload, so Modern Portrait (1) shows exactly the single
   uploader it always has and Together (4) grows extra tiles, with one shared
   file serving both products. A new style needs no theme change whatsoever.
--------------------------------------------------------------------------- */

(function () {
  "use strict";

  var FRAME_SWATCHES = {
    "print only": { hex: "#FFFFFF", outline: true },
    "white": { hex: "#F4F3F0" },
    "natural wood": { hex: "#C7A277" },
    "dark wood": { hex: "#5B3B26" },
    "black": { hex: "#1C1C1C" },
    "black metal": { hex: "#2C2F33", sheen: true }
  };

  /* ---- helpers ------------------------------------------------------------ */

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  // '12x16"' becomes '30x40cm', rounded to the nearest 5cm because that's how
  // frames are actually sold and 30.48 on a button looks like a bug.
  function inchesToCm(label) {
    var m = String(label).match(/(\d+)\s*[x\u00d7]\s*(\d+)/i);
    if (!m) return null;
    var w = Math.round((parseInt(m[1], 10) * 2.54) / 5) * 5;
    var h = Math.round((parseInt(m[2], 10) * 2.54) / 5) * 5;
    return w + "\u00d7" + h + "cm";
  }

  function cleanInches(label) {
    var m = String(label).match(/(\d+)\s*[x\u00d7]\s*(\d+)/i);
    return m ? m[1] + "\u00d7" + m[2] + '"' : String(label);
  }

  function norm(s) { return String(s || "").trim().toLowerCase(); }

  /** The first item that passes the test, or the first item if none does. */
  function prefer(list, test) {
    for (var i = 0; i < list.length; i++) if (test(list[i])) return list[i];
    return list[0];
  }

  /* ---- the studio -------------------------------------------------------- */

  function Studio(root) {
    this.root = root;
    this.base = root.dataset.workerBase.replace(/\/$/, "");
    this.styleId = root.dataset.styleId;
    this.moneyFormat = root.dataset.moneyFormat || "${{amount}}";
    this.units = root.dataset.units === "cm" ? "cm" : "in";
    this.canvasFormat = root.dataset.canvasFormat || "Canvas";
    // Per-product heading text. Empty string means "use the style's own", which
    // is why these are read as trimmed strings rather than as presence checks:
    // Liquid always renders the attribute, blank setting or not.
    this.titleOverride = (root.dataset.titleOverride || "").trim();
    this.subOverride = (root.dataset.subOverride || "").trim();

    this.variants = this.readJson("[data-pp-variants]", []);
    this.mockups = this.readJson("[data-pp-mockups]", []);
    this.gallery = this.readJson("[data-pp-gallery]", []);
    this.digital = this.readJson("[data-pp-digital]", null);
    if (this.digital && !this.digital.id) this.digital = null;

    this.style = null;
    // ALWAYS AN ARRAY, even for single-photo styles. One code path for both is
    // what keeps Modern Portrait from being a special case that rots.
    this.files = [];
    this.spec = null;
    this.handoff = null;
    this.selection = null;
    this.thumbUrls = [];
    this.artUrl = null;      // composited poster as a data URL
    this.activeMockup = 0;

    var raw = (root.dataset.lines || "").split(/\r?\n/).map(function (l) { return l.trim(); }).filter(Boolean);
    this.lines = raw.length ? raw : DEFAULT_LINES;
    // STARTS ON A FRAMED 18x24 POSTER, the middle of the range, rather than the
    // cheapest thing on the page. It used to open on Digital, smallest size,
    // Print only, so anyone who didn't change anything bought the cheapest
    // order there is. Falls back to the old behaviour if there is no poster.
    var poster = null;
    this.variants.forEach(function (v) { if (!poster && /poster/i.test(v.format || "")) poster = v.format; });
    this.chosenFormat = poster || (this.digital ? "digital" : (this.variants[0] || {}).format);
    this.chosenSize = null;
    this.chosenFrame = null;

    // Dialogs are moved to <body>, so look them up document-wide once they've
    // been portalled. Everything else stays scoped to this section.
    var q = function (name) {
      return root.querySelector("[data-pp-" + name + "]") ||
             document.querySelector("[data-pp-" + name + "]");
    };
    this.el = {
      options: q("options"), name: q("name"), fonts: q("fonts"),
      inks: q("inks"), inkField: q("inkfield"), fontField: q("fontfield"),
      drop: q("drop"), file: q("file"), thumb: q("thumb"), remove: q("remove"),
      generate: q("generate"), bar: q("bar"), error: q("error"),
      stage: q("stage"), thumbs: q("thumbs"), caption: q("caption"),
      ctaLabel: q("cta-label"), spin: q("spin"),
      from: q("from"), nameError: q("name-error"), photoError: q("photo-error"),
      prev: q("prev"), next: q("next"), eta: q("eta"),
      configure: q("configure"), result: q("result"),
      personalise: q("personalise"), personaliseSlot: q("personalise-slot"),
      formats: q("formats"), sizeField: q("size-field"), sizes: q("sizes"),
      frameField: q("frame-field"), frames: q("frames"),
      printOptions: q("print-options"),
      frameLabel: q("frame-label"), sizeLabel: q("size-label"),
      summary: q("summary"), add: q("add"), buy: q("buy"),
      changes: q("changes"), back: q("back"),
      loseModal: q("lose-modal"),
      title: q("title"), sub: q("sub"),
      save: q("save"), skip: q("skip"), saveError: q("save-error"),
      guideModal: q("guide-modal"), guideTitle: q("guide-title"), guideImg: q("guide-img"),
      saveModal: q("save-modal"), saveEmail: q("save-email"), saveConsent: q("save-consent"),
      cartModal: q("cart-modal"), cartImg: q("cart-img"), cartName: q("cart-name"),
      cartLine: q("cart-line"), cartAgain: q("cart-again"),
      saveSend: q("save-send"), saveCancel: q("save-cancel"),
      saveForm: q("save-form"), saveDone: q("save-done"),
      turnstile: q("turnstile"),
      limitDialog: q("limit-dialog"), limitText: q("limit-text"),
      changesDialog: q("changes-dialog"), changesPanel: q("changes-panel"),
      changesDone: q("changes-done"),
      chBody: q("ch-body"), chCount: q("ch-count"),
      chPet: q("ch-pet"), chStyle: q("ch-style"), chPreview: q("ch-preview"), chPage: q("ch-page"),
      sizeGuide: q("size-guide"), frameGuide: q("frame-guide")
    };
    this.el.thumbImg = this.el.thumb ? this.el.thumb.querySelector("img") : null;
    // The form itself is rendered by Shopify's {% form 'contact' %} tag, so grab
    // whatever element that produced rather than assuming its attributes.
    // Remember the resting label so it can be put back after a generation.
    root.dataset.ctaLabel = this.el.ctaLabel ? this.el.ctaLabel.textContent.trim() : "See my portrait";

    this.renderEta();
    this.portalModals();
    this.preloadGuides();
    this.showChangesConfirmed();
    this.initTurnstile();
    this.bind();
    this.renderStage();
    this.loadStyle();
  }

  // A position:fixed backdrop only covers the viewport when no ancestor has a
  // transform, and themes routinely put transforms on sections for scroll
  // animations. Moving the dialogs to <body> is the only reliable fix.
  Studio.prototype.portalModals = function () {
    this.root.querySelectorAll("[data-pp-modal-el]").forEach(function (m) {
      document.body.appendChild(m);
    });
  };

  // A real date beats "3 to 5 days": people can picture a Tuesday. Counted in
  // business days from today, so a Friday order doesn't promise a Sunday.
  Studio.prototype.renderEta = function () {
    var el = this.el.eta;
    if (!el) return;

    // A posted date is nonsense for a download: they get it the moment they pay.
    if (this.chosenFormat === "digital") {
      el.textContent = this.root.dataset.etaDigital || "Delivered instantly";
      return;
    }
    var d = this.root.dataset;
    var min = parseInt(d.etaMin, 10) || 3;
    var max = parseInt(d.etaMax, 10) || 5;

    var addBusiness = function (days) {
      var date = new Date();
      var added = 0;
      while (added < days) {
        date.setDate(date.getDate() + 1);
        var wd = date.getDay();
        if (wd !== 0 && wd !== 6) added++;
      }
      return date;
    };

    var fmt = function (date) {
      return date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
    };

    var a = addBusiness(min), b = addBusiness(max);
    el.textContent = (d.etaPrefix || "Est. delivery") + " " + fmt(a) + " \u2013 " + fmt(b);
  };

  Studio.prototype.readJson = function (sel, fallback) {
    var node = this.root.querySelector(sel);
    if (!node) return fallback;
    try { return JSON.parse(node.textContent); } catch (e) { return fallback; }
  };

  Studio.prototype.money = function (cents) {
    return this.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2));
  };

  Studio.prototype.bind = function () {
    var self = this;
    var el = this.el;

    el.drop.addEventListener("click", function () { el.file.click(); });
    el.drop.addEventListener("dragover", function (e) { e.preventDefault(); el.drop.classList.add("is-over"); });
    el.drop.addEventListener("dragleave", function () { el.drop.classList.remove("is-over"); });
    el.drop.addEventListener("drop", function (e) {
      e.preventDefault();
      el.drop.classList.remove("is-over");
      if (e.dataTransfer.files) self.addFiles(e.dataTransfer.files);
    });
    el.file.addEventListener("change", function (e) {
      if (e.target.files) self.addFiles(e.target.files);
      e.target.value = "";
    });
    // The remove button is bound per tile inside renderPhotos, because there is
    // one per photo and each needs to know its own index. The markup's original
    // single button is the first tile's, and it gets its listener there.

    el.generate.addEventListener("click", function () { self.generate(); });
    if (el.prev) el.prev.addEventListener("click", function () { self.step(-1); });
    if (el.next) el.next.addEventListener("click", function () { self.step(1); });
    el.add.addEventListener("click", function () { self.addToCart(); });
    // Buy it now: the same add, but skipping the confirmation and going straight
    // to checkout. For someone who has already decided, the panel is friction.
    if (el.buy) {
      el.buy.addEventListener("click", function () { self.addToCart(true); });
    }

    // Starting over is destructive, so it asks first, and the prompt doubles as
    // an email capture. There's deliberately no "generate again with the same
    // photo" button: an identical run costs real money and returns much the
    // same picture, so the only way back is to change something.
    // STRAIGHT BACK, NO CONFIRMATION.
    //
    // There used to be a dialog here warning that starting over would clear the
    // portrait, the photo and the name, and offering to email it first. That
    // warning is now false: every generation is kept in the strip under the studio,
    // so nothing is destroyed and there is nothing to rescue. Asking permission to
    // do something harmless is just friction.
    //
    // The email is still offered, by the "Save this design" button on the result
    // step, where it is an upgrade rather than a warning.
    // Confirm first, because Start over is now irreversible — there is no saved
    // designs strip to get the portrait back from. Only when there IS a portrait
    // to lose: on the configure step the button is just navigation.
    el.back.addEventListener("click", function () {
      if (!self.artUrl || !el.loseModal) return self.resetAll();
      self.open(el.loseModal);
    });
    if (el.loseModal) {
      var loseSave = el.loseModal.querySelector("[data-pp-lose-save]");
      if (loseSave) {
        loseSave.addEventListener("click", function () {
          // Straight into the email step. Closing back to the portrait would
          // leave them exactly where they were with the warning unanswered.
          self.close(el.loseModal);
          if (el.saveForm) el.saveForm.hidden = false;
          if (el.saveDone) el.saveDone.hidden = true;
          self.open(el.saveModal);
        });
      }
      var loseGo = el.loseModal.querySelector("[data-pp-lose-confirm]");
      if (loseGo) {
        loseGo.addEventListener("click", function () {
          self.close(el.loseModal);
          self.resetAll();
        });
      }
    }

    if (el.save) {
      el.save.addEventListener("click", function () {
        if (el.saveForm) el.saveForm.hidden = false;
        if (el.saveDone) el.saveDone.hidden = true;
        self.open(el.saveModal);
      });
    }
    if (el.skip) el.skip.addEventListener("click", function () { self.skipGeneration(); });

    if (el.changes) {
      el.changes.addEventListener("click", function () { self.openChanges(); });
    }
    // Listen to the submit WITHOUT cancelling it. Cancelling is what would let us
    // delay the navigation, but the submit event is exactly what Shopify's spam
    // protection hooks to inject its token, so interfering breaks it.
    //
    // Straight to the thank-you rather than a "sending" state. Shopify's response
    // is quick enough that a sending state is a flicker nobody can read, and this
    // way whatever the customer glimpses before the page turns over says the same
    // thing as the panel waiting for them afterwards.
    var chForm = el.changesDialog && el.changesDialog.querySelector("form");
    if (chForm) {
      chForm.addEventListener("submit", function () {
        if (el.changesPanel) el.changesPanel.hidden = true;
        if (el.changesDone) el.changesDone.hidden = false;
      });
    }

    if (el.limitDialog) {
      el.limitDialog.querySelectorAll("[data-pp-limit-close]").forEach(function (b) {
        b.addEventListener("click", function () { self.closeDialog(el.limitDialog); });
      });
    }

    // More than one close button, so bind them all.
    if (el.changesDialog) {
      el.changesDialog.querySelectorAll("[data-pp-ch-close]").forEach(function (b) {
        b.addEventListener("click", function () { self.closeDialog(el.changesDialog); });
      });
    }
    if (el.chBody) {
      el.chBody.addEventListener("input", function () {
        if (el.chCount) el.chCount.textContent = String(el.chBody.value.length);
      });
    }
    if (el.saveCancel) el.saveCancel.addEventListener("click", function () { self.close(el.saveModal); });

    // "Create another" closes the panel and resets, rather than reloading: a
    // reload would lose the styles fetch and the warmed container for no reason.
    if (el.cartAgain) {
      el.cartAgain.addEventListener("click", function () {
        self.close(el.cartModal);
        self.resetAll();
        if (self.root.scrollIntoView) {
          self.root.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    }
    if (el.saveSend) {
      el.saveSend.addEventListener("click", function () { self.submitSave(); });
    }

    if (el.sizeGuide) el.sizeGuide.addEventListener("click", function () { self.openGuide("size"); });
    if (el.frameGuide) el.frameGuide.addEventListener("click", function () { self.openGuide("frame"); });

    root_delegateClose(this.root, function (modal) { self.close(modal); });
    document.addEventListener("keydown", function (e) {
      if (e.key !== "Escape") return;
      [el.guideModal, el.saveModal, el.cartModal].forEach(function (m) { if (m) self.close(m); });
    });
  };

  function root_delegateClose(root, close) {
    document.querySelectorAll("[data-pp-modal-el]").forEach(function (modal) {
      modal.addEventListener("click", function (e) {
        if (e.target === modal || e.target.hasAttribute("data-pp-close")) close(modal);
      });
    });
  }

  Studio.prototype.open = function (modal) {
    if (!modal) return;
    modal.hidden = false;
    document.body.style.overflow = "hidden";
  };

  Studio.prototype.close = function (modal) {
    if (!modal || modal.hidden) return;

    // ANIMATE OUT, THEN HIDE. Setting hidden immediately removes the element in a
    // single frame, which is why these snapped shut while opening faded in — the
    // opening animation had an element to run on and the closing one did not.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      modal.hidden = true;
      document.body.style.overflow = "";
      return;
    }
    modal.classList.add("is-closing");
    window.setTimeout(function () {
      modal.classList.remove("is-closing");
      modal.hidden = true;
      document.body.style.overflow = "";
    }, 160);
  };

  /** The same, for a native <dialog>, which closes through its own method. */
  Studio.prototype.closeDialog = function (dlg) {
    if (!dlg || !dlg.open) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return dlg.close();
    dlg.classList.add("is-closing");
    window.setTimeout(function () {
      dlg.classList.remove("is-closing");
      dlg.close();
    }, 160);
  };

  // Guides are per finish, because a canvas size chart and a poster size chart
  // are different pictures. Falls back to the poster image when a canvas one
  // hasn't been added yet, so half-finished setups still show something useful.
  Studio.prototype.guideSrc = function (which) {
    var d = this.root.dataset;
    var isCanvas = norm(this.chosenFormat) === norm(this.canvasFormat);
    if (which === "frame") {
      return (isCanvas ? d.frameGuideCanvas : d.frameGuidePoster) || d.frameGuidePoster || "";
    }
    return (isCanvas ? d.sizeGuideCanvas : d.sizeGuidePoster) || d.sizeGuidePoster || "";
  };

  /**
   * Fetches the size and frame guides quietly after the page has settled.
   *
   * They used to be requested only when the dialog opened, so the customer waited
   * on a cold download of a 1600px image at exactly the moment they had asked to
   * see it — several seconds on a phone. Warming them costs nothing on a page that
   * has already finished loading, and the dialog then opens instantly.
   */
  Studio.prototype.preloadGuides = function () {
    var d = this.root.dataset;
    var urls = [d.sizeGuidePoster, d.sizeGuideCanvas, d.frameGuidePoster, d.frameGuideCanvas];
    var start = function () {
      urls.forEach(function (u) {
        if (!u) return;
        var img = new Image();
        img.decoding = "async";
        img.src = u;
      });
    };
    // After load, so this never competes with the portrait or the page itself.
    if (document.readyState === "complete") window.setTimeout(start, 1200);
    else window.addEventListener("load", function () { window.setTimeout(start, 1200); });
  };

  Studio.prototype.openGuide = function (which) {
    var src = this.guideSrc(which);
    if (!src || !this.el.guideModal) return;
    this.el.guideTitle.textContent = which === "frame" ? "Frame guide" : "Print size guide";
    this.el.guideImg.src = src;
    this.open(this.el.guideModal);
  };

  /* ---- style -------------------------------------------------------------- */

  // Opens the dialog and fills the hidden fields. Nothing intercepts the submit:
  // the customer clicks Shopify's own button, which is precisely why this version
  // works where four scripted ones didn't.
  Studio.prototype.openChanges = function () {
    var el = this.el;
    if (!el.changesDialog) return;

    if (el.changesPanel) el.changesPanel.hidden = false;
    if (el.changesDone) el.changesDone.hidden = true;

    // Shopify includes every contact[...] field in the notification email, so
    // the message names the pet, the style and links to the portrait without the
    // customer typing any of it.
    if (el.chPet) el.chPet.value = (this.selection && this.selection.displayName) || "not given";
    if (el.chStyle) el.chStyle.value = this.style ? this.style.name : "unknown";
    if (el.chPreview) el.chPreview.value = this.posterSrc() || "not available";
    if (el.chPage) el.chPage.value = window.location.href;

    if (typeof el.changesDialog.showModal === "function") el.changesDialog.showModal();
    else el.changesDialog.setAttribute("open", "");
  };

  Studio.prototype.showChangesConfirmed = function () {
    if (window.location.search.indexOf("contact_posted=true") === -1) return;
    var el = this.el;
    if (!el.changesDialog) return;

    // The submit reloads the page, so without this the customer lands back on a
    // studio with no portrait and no sign anything happened. Show the thank-you
    // straight away and give them a few seconds with it before it clears.
    if (el.changesPanel) el.changesPanel.hidden = true;
    if (el.changesDone) el.changesDone.hidden = false;
    if (typeof el.changesDialog.showModal === "function") el.changesDialog.showModal();

    setTimeout(function () {
      if (el.changesDialog.open) el.changesDialog.close();
    }, 7000);

    // Tidy the URL so a refresh doesn't show the thank-you again.
    if (window.history && window.history.replaceState) {
      var clean = window.location.pathname +
        window.location.search.replace(/[?&]contact_posted=true/, "").replace(/^&/, "?");
      window.history.replaceState({}, "", clean);
    }
  };

  Studio.prototype.submitSave = function () {
    var self = this;
    var el = this.el;
    var showErr = function (msg) {
      if (!el.saveError) return;
      el.saveError.textContent = msg || "";
      el.saveError.hidden = !msg;
    };

    if (this.testMode) {
      return showErr("This is the test image. Generate a real portrait to test saving.");
    }

    var email = (el.saveEmail.value || "").trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return showErr("Please enter a valid email address.");
    // Required, not optional: this is a marketing subscription, so the consent
    // has to be explicit here and it's checked again on the server.
    if (el.saveConsent && !el.saveConsent.checked) return showErr("Please tick the box to agree to marketing emails.");
    // A resumed portrait has an artUrl but no spec, so check for the image
    // rather than the generation response.
    var previewUrl = this.posterSrc();
    if (!this.handoff || !previewUrl) return showErr("Generate your portrait first.");
    showErr("");

    el.saveSend.disabled = true;
    var resting = el.saveSend.textContent;
    el.saveSend.textContent = "Sending\u2026";

    // Settle any edits first, exactly as Add to cart does. The email carries the
    // poster, so sending before finalising would email the colour they generated
    // on rather than the one they picked.
    var self = this;
    this.finaliseDesign().then(function () {
      self.sendSaveEmail(email, el, resting, showErr);
    });
  };

  Studio.prototype.sendSaveEmail = function (email, el, resting, showErr) {
    var self = this;
    var previewUrl = this.posterSrc();

    fetch(this.base + "/api/save", {
      method: "POST",
      mode: "cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: email,
        consent: !!(el.saveConsent && el.saveConsent.checked),
        styleId: this.style.id,
        selection: this.selection,
        previewUrl: previewUrl,
        handoffToken: this.handoff,
        petName: this.selection.displayName || ""
      })
    })
      .then(function (r) {
        return r.json().then(function (b) {
          if (!r.ok) throw new Error(b.error || "Couldn't save your portrait.");
          return b;
        });
      })
      .then(function () {
        // Swap the form for the success panel rather than closing the modal:
        // people missed the old one-line confirmation entirely.
        if (el.saveForm) el.saveForm.hidden = true;
        if (el.saveDone) el.saveDone.hidden = false;
      })
      .catch(function (err) { showErr(err.message); })
      .then(function () {
        el.saveSend.disabled = false;
        el.saveSend.textContent = resting;
      });
  };

  // If the URL carries ?resume=<token>, pull the saved portrait back and drop
  // straight into the result step. No generation, so returning from the email
  // costs nothing.
  Studio.prototype.tryResume = function () {
    var self = this;
    var m = window.location.search.match(/[?&]resume=([a-f0-9]{32})/);
    if (!m) return;
    var token = m[1];

    fetch(this.base + "/api/save/" + token, { mode: "cors" })
      .then(function (r) {
        return r.json().then(function (b) {
          if (!r.ok) throw new Error(b.error || "This link has expired.");
          return b.saved;
        });
      })
      .then(function (saved) {
        self.selection = saved.selection;
        self.handoff = saved.handoffToken;
        self.spec = null;
        self.artUrl = saved.previewUrl;   // already composited and watermarked
        self.activeMockup = 0;
        self.renderOptions();
        self.setPhase("result");
        self.renderChoices();
        self.renderStage();
        self.syncStageToFrame();
      })
      .catch(function (err) {
        self.showError(err.message + " Upload a photo to make a new one.");
      });
  };

  /* ---- add-ons --------------------------------------------------------------
     A style that needs more than the core does (the magazine covers) ships its
     own small script, loaded only on its own product template, which registers
     itself on window.PPStudioAddons. The core asks it at a handful of points and
     carries on as normal when nothing answers, so every other product behaves
     exactly as it did before this existed.

     An add-on is { applies(studio), ...hooks }. Each hook gets the studio first.
  --------------------------------------------------------------------------- */
  Studio.prototype.addon = function () {
    if (!this.style) return null;
    if (this._addon !== undefined) return this._addon;
    var self = this, found = null;
    (window.PPStudioAddons || []).forEach(function (a) {
      if (!found && a && a.applies && a.applies(self)) found = a;
    });
    this._addon = found;
    return found;
  };

  /** Run a hook if the add-on has one. Undefined when nothing answers. */
  Studio.prototype.hook = function (name) {
    var a = this.addon();
    if (!a || typeof a[name] !== "function") return undefined;
    return a[name].apply(a, [this].concat([].slice.call(arguments, 1)));
  };

  Studio.prototype.loadStyle = function () {
    var self = this;
    fetch(this.base + "/api/styles", { mode: "cors" })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var found = null;
        data.styles.forEach(function (s) { if (s.id === self.styleId) found = s; });
        if (!found) throw new Error("Style not found: " + self.styleId);
        self.style = found;
        self.selection = self.defaultSelection(found);
        self.renderOptions();
        self.setPhase("configure");
        self.renderChoices();
        // The personalise fields are kept invisible until now, so the name box
        // and its "Optional" tag can't show before the style decides what goes
        // above them and whether they belong at all.
        self.root.classList.add("is-ready");
        self.tryResume();
      })
      .catch(function (err) {
        console.error("[pp-studio]", err);
        self.root.classList.add("is-ready");
        self.showError("The portrait studio couldn't load. Please refresh the page.");
      });
  };

  Studio.prototype.defaultSelection = function (style) {
    var options = {};
    style.optionGroups.forEach(function (g) {
      options[g.id] = g.kind === "multiselect" ? [] : g.defaultValue;
    });
    var sel = {
      styleId: style.id,
      orientation: style.orientations[0],
      options: options,
      nameFontId: style.nameTreatments[0].fontId,
      nameColorId: (style.nameColors && style.nameColors[0] && style.nameColors[0].id) || "auto",
      displayName: ""
    };
    this.hook("initSelection", sel);
    return sel;
  };

  Studio.prototype.renderOptions = function () {
    var self = this;
    var html = "";

    this.style.optionGroups.forEach(function (g) {
      var chosen = null;
      g.options.forEach(function (o) { if (o.id === self.selection.options[g.id]) chosen = o; });
      html += '<div class="pp-field"><div class="pp-label">' + esc(g.label) +
        (g.kind === "swatch" && chosen ? '<span class="pp-hint">' + esc(chosen.label) + "</span>" : "") +
        (g.kind === "text" ? '<span class="pp-hint" data-count="' + esc(g.id) + '" style="margin-left:auto;opacity:.6">' +
          String(self.selection.options[g.id] || "").length + "/" + (g.maxLength || 40) + "</span>" : "") + "</div>";
      if (g.kind === "swatch") {
        html += '<div class="pp-swatches">';
        g.options.forEach(function (o) {
          html += '<button type="button" class="pp-swatch' + (self.selection.options[g.id] === o.id ? " on" : "") +
            '" style="background:' + o.hex + '" title="' + esc(o.label) + '" aria-label="' + esc(o.label) +
            '" data-group="' + esc(g.id) + '" data-value="' + esc(o.id) + '"></button>';
        });
        html += "</div>";
      } else if (g.kind === "select") {
        // A DROPDOWN, NOT PILLS. Pills are right for two or three choices you
        // can compare at a glance; ten of them is a wall of buttons that pushes
        // everything else off the screen. A select stays one line whatever the
        // list length, and on a phone it opens the native picker.
        html += '<select class="pp-select" data-group="' + esc(g.id) + '">';
        g.options.forEach(function (o) {
          html += '<option value="' + esc(o.id) + '"' +
            (self.selection.options[g.id] === o.id ? " selected" : "") + ">" + esc(o.label) + "</option>";
        });
        html += "</select>";
      } else if (g.kind === "text") {
        // TYPED WORDS, drawn by the browser like the name, so they stay editable
        // after generating. No options to choose between.
        // EMPTY UNTIL THEY TYPE. The default shows as the greyed placeholder and
        // is what gets used if they leave it, here and on the server alike.
        var tv = self.selection.options[g.id] || "";
        if (tv === g.defaultValue || tv === g.placeholder || tv === g.autoValue) tv = "";
        html += '<input class="pp-text" type="text" data-group="' + esc(g.id) + '"' +
          ' maxlength="' + (g.maxLength || 40) + '"' +
          ' placeholder="' + esc(g.placeholder || "") + '"' +
          ' value="' + esc(tv) + '">' +
          '<p class="pp-fielderr" data-err="' + esc(g.id) + '" hidden>Please fill this in.</p>';
      } else {
        html += '<div class="pp-pills">';
        g.options.forEach(function (o) {
          html += '<button type="button" class="pp-pill' + (self.selection.options[g.id] === o.id ? " on" : "") +
            '" data-group="' + esc(g.id) + '" data-value="' + esc(o.id) + '">' + esc(o.label) + "</button>";
        });
        html += "</div>";
      }
      html += "</div>";
    });

    this.el.options.innerHTML = html;
    // OPTIONS THAT ONLY THE MODEL CAN CHANGE ARE HIDDEN ONCE A PORTRAIT EXISTS.
    //
    // A swatch is a colour the browser paints, so it stays live. Expression is
    // painted INTO the animal and cannot be altered without generating again —
    // offering it here would promise something a click cannot deliver. It is
    // still fully available before generating, and Start over brings it back.
    this.el.options.querySelectorAll(".pp-field").forEach(function (field, i) {
      var g = self.style.optionGroups[i];
      if (!g) return;
      // Typed text is a live edit too, but only when the browser is drawing the
      // poster. On a flat poster from the server there is nothing to redraw.
      var live = g.kind === "swatch" ||
        (g.kind === "text" && self.spec && self.spec.layer === "cutout");
      field.hidden = self.phase === "result" && !live;
    });
    // The dropdown changes on "change", not "click", so it needs its own binding
    // — and it is NOT a live edit: the drink is painted into the scene, so
    // changing it needs a new generation. No recompose here.
    this.el.options.querySelectorAll("select[data-group]").forEach(function (sel) {
      sel.addEventListener("change", function () {
        self.selection.options[sel.dataset.group] = sel.value;
        self.syncGalleryToOption(sel.dataset.group, sel.value);
      });
    });

    this.el.options.querySelectorAll("input[data-group]").forEach(function (inp) {
      inp.addEventListener("input", function () {
        self.selection.options[inp.dataset.group] = inp.value;
        var count = self.el.options.querySelector('[data-count="' + inp.dataset.group + '"]');
        if (count) count.textContent = inp.value.length + "/" + inp.maxLength;
        var err = self.el.options.querySelector('[data-err="' + inp.dataset.group + '"]');
        if (err && inp.value.trim()) { err.hidden = true; inp.classList.remove("is-invalid"); }
        if (self.spec && self.spec.layer === "cutout") {
          clearTimeout(self._textTimer);
          self._textTimer = setTimeout(function () { self.recompose(); }, 140);
        }
      });
    });

    this.el.options.querySelectorAll("button[data-group]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        self.selection.options[btn.dataset.group] = btn.dataset.value;
        self.renderOptions();
        // A swatch change used to mean regenerating, because the background was
        // painted into the artwork. With a cutout layer it is a repaint of the
        // canvas underneath the animal, so it is instant and free.
        self.recompose();
      });
    });

    var fhtml = "";
    this.style.nameTreatments.forEach(function (n) {
      fhtml += '<button type="button" class="pp-pill' + (self.selection.nameFontId === n.fontId ? " on" : "") +
        '" data-font="' + esc(n.fontId) + '" style="font-family:' + n.cssFamily + '">' + esc(n.label) + "</button>";
    });
    this.el.fonts.innerHTML = fhtml;
    this.el.fonts.querySelectorAll("[data-font]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        self.selection.nameFontId = btn.dataset.font;
        self.renderOptions();
        self.recompose();
      });
    });

    // ---- name style and ink -------------------------------------------------
    // Both are hidden while the name box is empty: a font picker and a colour
    // picker for lettering that will not be drawn are two more things to read
    // past on a form whose only required field is the photo.
    //
    // The ink row additionally needs the style to offer a palette worth choosing
    // from — a single option is not a choice.
    var named = !!String(this.selection.displayName || "").trim();
    var inks = this.style.nameColors || [];
    // A class, not the `hidden` attribute: display:none cannot be transitioned,
    // and these two grow in and out as the name box fills. The CSS holds them
    // collapsed by default, so nothing flashes open before this runs.
    // One font is not a choice either, the same rule as the ink row.
    var fontsOffered = (this.style.nameTreatments || []).length > 1;
    this.setFieldOpen(this.el.fontField, named && fontsOffered);
    this.setFieldOpen(this.el.inkField, named && inks.length > 1);

    if (this.el.inks && inks.length > 1) {
      var ihtml = "";
      inks.forEach(function (c) {
        var dot = c.hex
          ? '<span class="pp-ink__dot" style="background:' + esc(c.hex) + '"></span>'
          : '<span class="pp-ink__dot is-auto"></span>';
        ihtml += '<button type="button" class="pp-ink' +
          (self.selection.nameColorId === c.id ? " on" : "") +
          '" data-ink="' + esc(c.id) + '">' + dot + esc(c.label) + "</button>";
      });
      this.el.inks.innerHTML = ihtml;
      this.el.inks.querySelectorAll("[data-ink]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          self.selection.nameColorId = btn.dataset.ink;
          self.renderOptions();
          self.recompose();
        });
      });
    }

    // A STYLE WITH nameMaxLength 0 DOES NOT TAKE A NAME AT ALL.
    //
    // The Happy Hour is a scene rather than a portrait — a name floating on the
    // wall above the chair reads as a caption stuck onto a painting — so the
    // whole field goes, along with the font and ink pickers that only exist to
    // style it. Hidden here rather than in the markup because the styles share
    // one section and one script.
    var takesName = (this.style.nameMaxLength || 0) > 0;
    var nameField = this.el.name && this.el.name.closest(".pp-field");
    if (nameField) nameField.hidden = !takesName;
    if (!takesName) {
      this.selection.displayName = "";
      this.setFieldOpen(this.el.fontField, false);
      this.setFieldOpen(this.el.inkField, false);
    }

    this.el.name.maxLength = this.style.nameMaxLength;
    this.el.name.value = this.selection.displayName || "";
    this.el.name.oninput = function () {
      var had = !!String(self.selection.displayName || "").trim();
      self.selection.displayName = self.el.name.value;
      var has = !!String(self.el.name.value || "").trim();
      // Only re-render when the name crosses between empty and not, since
      // rebuilding the options on every keystroke would steal focus from the box.
      if (had !== has) {
        self.setFieldOpen(self.el.fontField, has && (self.style.nameTreatments || []).length > 1);
        self.setFieldOpen(self.el.inkField, has && (self.style.nameColors || []).length > 1);
      }
      self.updateGenerateState();
      // Redraw as they type, but not on EVERY keystroke: composing a 1152x1536
      // canvas and serialising it to a data URL takes long enough that a fast
      // typist would feel the lag. A short debounce reads as immediate.
      if (self.spec && self.spec.layer === "cutout") {
        clearTimeout(self._nameTimer);
        self._nameTimer = setTimeout(function () { self.recompose(); }, 140);
      }
    };
    this.updateGenerateState();
    this.hook("afterRenderOptions");
    // Now the style is known, so the uploader can size itself to maxPhotos. On a
    // single-photo style this is a no-op that leaves the markup exactly as the
    // Liquid rendered it.
    this.renderPhotos();
  };

  /** Grow a collapsible field in or out. Tolerates a missing element. */
  Studio.prototype.setFieldOpen = function (el, open) {
    if (!el) return;
    el.classList.toggle("is-open", !!open);
    // Belt and braces for anything that reads the attribute rather than the
    // class: the styling is the class's job, this keeps the two in step.
    el.hidden = false;
  };

  /* ---- upload -------------------------------------------------------------
     Multi-photo, driven entirely by the style's own maxPhotos, which arrives in
     the /api/styles payload. Modern Portrait is 1 and behaves precisely as it
     always has: one tile, one file, no multiple attribute on the input, and no
     way to add a second. Together is 4 and grows tiles as photos are chosen.

     ONE SUBJECT PER PHOTO is the contract the prompt depends on. The Worker
     hands each image to the model as a separate reference and tells it to work
     out for itself which is a person and which is an animal, so the COUNT of
     images is the count of subjects. Two people in one tile reads as one
     subject and somebody goes missing from the painting.
  --------------------------------------------------------------------------- */

  Studio.prototype.maxPhotos = function () {
    var n = this.style && this.style.maxPhotos ? parseInt(this.style.maxPhotos, 10) : 1;
    return n > 0 ? n : 1;
  };

  Studio.prototype.addFiles = function (list) {
    var incoming = [].slice.call(list || []);
    var max = this.maxPhotos();
    this.showError(null);

    for (var i = 0; i < incoming.length; i++) {
      var f = incoming[i];
      if (this.files.length >= max) {
        this.showError(max === 1
          ? "This style takes one photo. Remove the current one to choose another."
          : "This style takes up to " + max + " photos.");
        break;
      }
      if (f.type && !/image\/(jpeg|png|webp|heic|heif)/.test(f.type)) {
        return this.showError("Please choose a JPG, PNG or HEIC photo.");
      }
      if (f.size > 20 * 1024 * 1024) {
        return this.showError("That photo is over 20MB. Try a smaller one.");
      }
      this.files.push(f);
    }
    this.renderPhotos();
    this.updateGenerateState();
  };

  Studio.prototype.removeAt = function (i) {
    this.files.splice(i, 1);
    this.showError(null);
    this.renderPhotos();
    this.updateGenerateState();
  };

  Studio.prototype.clearFiles = function () {
    this.files = [];
    this.showError(null);
    this.renderPhotos();
    this.updateGenerateState();
  };

  /**
   * Draws one tile per chosen photo, plus the drop zone while there is room.
   *
   * Extra tiles are CLONES of the theme's own thumbnail element, inserted into
   * the same parent. They therefore inherit the Liquid's markup and the CSS that
   * styles it, exactly. Building fresh markup here would put this file and the
   * section template both in charge of what a tile looks like, and the two would
   * drift apart the first time either was touched.
   */
  Studio.prototype.renderPhotos = function () {
    var self = this;
    var el = this.el;
    if (!el.thumb || !el.drop) return;

    var parent = el.thumb.parentNode;
    parent.querySelectorAll("[data-pp-thumb-extra]").forEach(function (n) { n.remove(); });

    // Revoke before rebuilding, or every re-render leaks an object URL.
    this.thumbUrls.forEach(function (u) { URL.revokeObjectURL(u); });
    this.thumbUrls = [];

    // Only offer the OS multi-select where the style can actually take several.
    if (el.file) el.file.multiple = this.maxPhotos() > 1;

    if (!this.files.length) {
      el.thumb.hidden = true;
      el.drop.hidden = false;
      el.drop.classList.remove("pp-drop--more");
      return;
    }

    this.files.forEach(function (f, i) {
      var url = URL.createObjectURL(f);
      self.thumbUrls.push(url);

      var tile = el.thumb;
      if (i > 0) {
        tile = el.thumb.cloneNode(true);
        tile.setAttribute("data-pp-thumb-extra", "");
      }
      // EVERY tile is moved in front of the drop zone, the original included.
      //
      // The Liquid renders the thumbnail AFTER the drop zone, which is invisible
      // on a single-photo style because only one of the two is ever on screen.
      // With several it read as chaos: the first photo appeared below the button
      // and the second above it. Reordering here rather than in the section keeps
      // one uploader serving both styles, and insertBefore on an element already
      // in the document MOVES it rather than copying, so this costs nothing.
      parent.insertBefore(tile, el.drop);
      tile.hidden = false;

      var img = tile.querySelector("img");
      if (img) img.src = url;

      // Replace the remove button with a copy of itself before binding. The
      // index it closes over changes on every render, and leaving the old
      // listener attached would delete the wrong photo after a removal.
      var rm = tile.querySelector("[data-pp-remove]");
      if (rm) {
        var fresh = rm.cloneNode(true);
        rm.parentNode.replaceChild(fresh, rm);
        fresh.addEventListener("click", function (e) {
          e.stopPropagation();
          self.removeAt(i);
        });
      }
    });

    // The drop zone disappears once the style's allowance is used up, which is
    // what stops a Modern Portrait customer ever seeing a second tile. Until then
    // it sits AFTER the photos as a compact "add another" tile: same element, same
    // click handler, just a smaller presentation driven by this class.
    el.drop.hidden = this.files.length >= this.maxPhotos();
    el.drop.classList.add("pp-drop--more");
  };

  // The button stays clickable even when things are missing. Disabling it just
  // leaves people staring at a dead button with no idea why; clicking it and
  // being told exactly which field is empty is far clearer.
  Studio.prototype.updateGenerateState = function () {
    this.el.generate.disabled = false;
    if (this.files.length) this.setFieldError("photo", false);
    if (this.selection && String(this.selection.displayName || "").trim()) this.setFieldError("name", false);
  };

  Studio.prototype.setFieldError = function (which, on) {
    var msg = which === "name" ? this.el.nameError : this.el.photoError;
    var field = which === "name" ? this.el.name : this.el.drop;
    if (msg) msg.hidden = !on;
    if (field) field.classList.toggle("is-invalid", !!on);
  };

  // Returns true when we're clear to spend a generation.
  //
  // The NAME IS OPTIONAL. It used to be required, which was wrong: plenty of
  // people want the portrait and not the lettering, and refusing to generate
  // until they type something turns a preference into an obstacle. Leave it
  // blank and the poster comes out with no name on it.
  //
  // ONE PHOTO IS ENOUGH, even on a four-photo style. Somebody who uploads only
  // themselves gets a portrait of themselves; refusing to generate until they
  // fill every tile would invent a requirement the painting does not have.
  Studio.prototype.validate = function () {
    var hasPhoto = this.files.length > 0;
    this.setFieldError("name", false);
    this.setFieldError("photo", !hasPhoto);

    if (!hasPhoto && this.el.drop) {
      if (this.el.drop.scrollIntoView) {
        this.el.drop.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
    // A style's add-on can ask for more, e.g. a cover needs a name.
    if (this.hook("validate") === false) return false;
    return hasPhoto;
  };

  // The Worker's own wording, because the personal cap and the global ceiling
  // say different things and only the server knows which one was hit.
  // Renders the invisible widget once, as soon as the Turnstile script has
  // loaded. Nothing visible happens; it just becomes ready to be asked.
  Studio.prototype.initTurnstile = function () {
    var el = this.el;
    var self = this;
    if (!el.turnstile) return;

    var tries = 0;
    (function waitForScript() {
      if (window.turnstile && typeof window.turnstile.render === "function") {
        self.turnstileId = window.turnstile.render(el.turnstile, {
          sitekey: el.turnstile.dataset.sitekey,
          size: "invisible",
          // "execute" means it sits idle until asked, which is what lets us take
          // a fresh token per generation rather than one stale one on page load.
          execution: "execute",
          callback: function (token) {
            if (self.turnstileResolve) { self.turnstileResolve(token); self.turnstileResolve = null; }
          },
          "error-callback": function () {
            // Let the generation go ahead without a token. The Worker decides
            // what to do about that; a Turnstile outage should not take the whole
            // studio down.
            if (self.turnstileResolve) { self.turnstileResolve(""); self.turnstileResolve = null; }
          },
        });
        return;
      }
      if (tries++ < 40) setTimeout(waitForScript, 150);
    })();
  };

  /** A fresh token, or "" if Turnstile is unavailable. Never rejects. */
  Studio.prototype.turnstileToken = function () {
    var self = this;
    if (!this.el.turnstile || !window.turnstile || this.turnstileId == null) {
      return Promise.resolve("");
    }
    return new Promise(function (resolve) {
      self.turnstileResolve = resolve;
      // Tokens are single use, so clear the last one before asking for another.
      try {
        window.turnstile.reset(self.turnstileId);
        window.turnstile.execute(self.turnstileId);
      } catch (e) {
        resolve("");
        return;
      }
      // Don't let a silent widget hang the generate button for ever.
      setTimeout(function () {
        if (self.turnstileResolve) { self.turnstileResolve(""); self.turnstileResolve = null; }
      }, 8000);
    });
  };

  /**
   * Confirms the add without leaving the page.
   *
   * Redirecting to /cart ended the session: the customer was looking at a receipt
   * instead of the studio, and a second portrait meant navigating back. This shows
   * what was added and offers both directions.
   */
  Studio.prototype.showAddedToCart = function (props, isDigital) {
    var el = this.el;
    if (!el.cartModal) return;               // panel not in the theme yet

    if (el.cartImg) {
      var src = props["_preview_url"];
      el.cartImg.hidden = !src;
      if (src) el.cartImg.src = src;
      el.cartImg.alt = (props["Pet name"] && props["Pet name"] !== "None")
        ? props["Pet name"] + "'s portrait" : "Your portrait";
    }
    if (el.cartName) {
      el.cartName.textContent = (props["Pet name"] && props["Pet name"] !== "None")
        ? props["Pet name"] : this.style.name;
    }
    if (el.cartLine) {
      // Reuses the summary's own wording, so the panel says exactly what the
      // order summary said a moment earlier rather than inventing a description.
      var bits = [this.style.name];
      if (isDigital) {
        bits.push(this.digital && this.digital.title ? this.digital.title : "Digital file");
      } else {
        if (this.chosenSize) bits.push(this.sizeLabelFor(this.chosenSize));
        // chosenFrame is already the label the customer clicked, which is what the
        // size label field shows too. No lookup needed, and inventing a
        // frameLabelFor() to match sizeLabelFor() would be a method that does
        // nothing.
        if (this.chosenFrame) bits.push(this.chosenFrame);
      }
      el.cartLine.textContent = bits.join(" · ");
    }

    this.open(el.cartModal);
  };

  Studio.prototype.showLimit = function (message) {
    var el = this.el;
    if (el.limitText) el.limitText.textContent = message;
    if (typeof el.limitDialog.showModal === "function") el.limitDialog.showModal();
    else el.limitDialog.setAttribute("open", "");
  };

  Studio.prototype.showError = function (msg) {
    this.el.error.textContent = msg || "";
    this.el.error.hidden = !msg;
  };

  // Back to a genuinely blank slate: no photo, no name, no portrait, options
  // back to their defaults. Anything left behind makes the second attempt feel
  // like a half-cleared form.
  Studio.prototype.resetAll = function () {
    // Prices and available formats come from the variant data, so a reset has to
    // let the cards be rebuilt rather than keeping stale markup.
    if (this.el && this.el.formats) delete this.el.formats.dataset.built;
    this.spec = null;
    this.handoff = null;
    this.artUrl = null;
    this.testMode = false;
    this.activeMockup = 0;
    this.clearFiles();
    if (this.style) this.selection = this.defaultSelection(this.style);
    if (this.el.name) this.el.name.value = "";
    this.chosenSize = null;
    this.chosenFrame = null;
    this.showError(null);
    if (this.renderOptions && this.style) this.renderOptions();
    this.setPhase("configure");
    this.renderChoices();
    this.renderStage();
    if (this.root.scrollIntoView) this.root.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Cheapest thing on the page, digital included, so the configure screen
  // answers "what does this cost" before anyone uploads anything.
  Studio.prototype.renderFromPrice = function () {
    var el = this.el.from;
    if (!el) return;
    var min = this.digital ? this.digital.price : null;
    this.variants.forEach(function (v) {
      if (min === null || v.price < min) min = v.price;
    });
    if (min === null) { el.hidden = true; return; }
    el.innerHTML = "<span>From</span>" + this.money(min);
    el.hidden = false;
  };

  Studio.prototype.setPhase = function (phase) {
    var isResult = phase === "result";
    this.phase = phase;
    this.el.configure.hidden = isResult;
    this.el.result.hidden = !isResult;

    // Background, name, name style and ink follow the customer to the result
    // view, because they now redraw the poster instead of requiring another
    // generation. MOVED rather than copied: the script has already bound
    // listeners to these exact nodes, and two copies of the markup would need
    // every one of them re-bound and would drift apart the first time one side
    // was edited.
    //
    // The photo uploader deliberately stays behind. Changing the photo is a new
    // portrait, not a new colour, and it belongs with Start over.
    var home = isResult ? this.el.personaliseSlot : this.el.configure;
    if (this.el.personalise && home && this.el.personalise.parentNode !== home) {
      if (isResult) home.appendChild(this.el.personalise);
      else home.insertBefore(this.el.personalise, home.firstChild);
    }
    // Re-run so the generation-only options hide on the way in and come back on
    // the way out.
    if (this.style && this.el.options) this.renderOptions();
    if (this.el.from) this.el.from.hidden = isResult || !this.variants.length;
    if (!isResult) this.renderFromPrice();
    if (isResult) {
      this.el.title.textContent = "Your portrait is ready.";
      this.el.sub.textContent = "Choose your size and framing below.";
    } else if (this.style) {
      // The section's own wording wins when it is set. The Worker's style name is
      // a catalogue label; the same style can be sold as two products that want to
      // say different things about themselves.
      this.el.title.textContent = this.titleOverride || this.style.name;
      this.el.sub.textContent = this.subOverride || this.style.tagline;
    }
  };

  /* ---- the stage: mockup carousel ----------------------------------------- */

  // Two different carousels, depending on whether a portrait exists yet.
  //
  // Before generation: the product's own photographs, straight from Shopify.
  // The customer sees real product imagery rather than an empty grey box, which
  // is what the competitor's opening carousel is doing.
  //
  // After generation: the frame mockups, with the customer's portrait placed
  // into each cutout, plus a plain unframed slide.
  /* ---- saved designs: REMOVED ---------------------------------------------

     There used to be a strip of the customer's own previous portraits under the
     stage, kept in localStorage, that they could tap to return to.

     It came out when the poster became a CUT-OUT plus a chosen colour rather
     than one finished image. A saved record held a URL, and that is no longer
     enough to describe a design — the colour, the name, the font and the ink all
     live outside the image now, the stored layer is transparent, and a restored
     entry brought back an artwork without the choices that made it look like
     anything. Reconstructing all of that on tap is a lot of machinery for a
     strip most people scrolled past.

     Start over now simply returns to the configure step with the product
     photography, which is the nicest image on the page anyway.
  ------------------------------------------------------------------------- */

  Studio.prototype.renderStage = function () {
    if (this.artUrl) return this.renderMockups();

    // The configure step always shows the product photography in the stage, first
    // visit or fifth — it is the nicest image on the page and there is no reason to
    // lose it. What changes is the STRIP UNDERNEATH: once they have generated
    // something, that becomes their own portraits instead of more example shots,
    // and tapping one takes them back to it.
    this.renderGallery();
  };

  Studio.prototype.renderGallery = function () {
    var self = this;
    var slides = "";
    var thumbs = "";

    this.gallery.forEach(function (g, i) {
      var on = i === self.activeMockup;
      slides += '<div class="pp-slide' + (on ? " on" : "") + '" data-slide="' + i + '">' +
        '<img class="pp-slide__bg" src="' + g.src + '" alt="' + esc(g.label || "") + '"></div>';
      thumbs += '<button type="button" class="pp-thumbnail' + (on ? " on" : "") + '" data-slide="' + i + '"' +
        ' aria-label="' + esc(g.label || "View") + '"><img src="' + g.src + '" alt=""></button>';
    });

    if (!slides) {
      slides = '<div class="pp-slide on"><div class="pp-stage__empty">Your portrait appears here</div></div>';
    }

    this.el.stage.innerHTML = slides;
    this.el.thumbs.innerHTML = this.gallery.length > 1 ? thumbs : "";
    this.bindThumbs();
    this.syncNav();
    if (this.el.caption) this.el.caption.textContent = "";
  };

  Studio.prototype.renderMockups = function () {
    var self = this;
    var slides = "";
    var thumbs = "";

    this.mockups.forEach(function (m, i) {
      var on = i === self.activeMockup;
      var body;
      if (m.overlay) {
        // Artwork underneath, frame PNG on top. The inset pulls the art in a
        // little so its edges tuck behind the moulding rather than peeking out.
        var ins = m.inset || 0;
        body =
          self.posterHTML(
            "top:" + ins + "%;left:" + ins + "%;width:" + (100 - ins * 2) + "%;height:" + (100 - ins * 2) + "%;" +
            (m.opacity ? "opacity:" + m.opacity + ";" : ""), "pp-slide__under") +
          (m.src ? '<img class="pp-slide__over" src="' + m.src + '" alt="' + esc(m.label || "") + '">' : "");
      } else {
        body =
          (m.src ? '<img class="pp-slide__bg" src="' + m.src + '" alt="' + esc(m.label || "") + '">' : "") +
          self.posterHTML(
            "top:" + m.top + "%;left:" + m.left + "%;width:" + m.width + "%;height:" + m.height + "%;" +
            (m.opacity ? "opacity:" + m.opacity + ";" : ""), "pp-slide__art");
      }
      slides += '<div class="pp-slide' + (on ? " on" : "") + '" data-slide="' + i + '">' + body + "</div>";

      var tbody = m.overlay
        ? self.posterHTML("", "") + (m.src ? '<img src="' + m.src + '" alt="">' : "")
        : (m.src ? '<img src="' + m.src + '" alt="">' : "") +
          self.posterHTML(
            "top:" + m.top + "%;left:" + m.left + "%;width:" + m.width + "%;height:" + m.height + "%;",
            "pp-thumbnail__art");
      thumbs += '<button type="button" class="pp-thumbnail' + (on ? " on" : "") + '" data-slide="' + i + '"' +
        ' aria-label="' + esc(m.label || "View") + '">' + tbody + "</button>";
    });

    // Plain slide: the artwork alone, no frame. Always last, and it's what
    // Print only and the digital file show.
    var plainOn = this.activeMockup === this.mockups.length;
    slides += '<div class="pp-slide pp-slide--plain' + (plainOn ? " on" : "") + '" data-slide="' + this.mockups.length + '">' +
      this.posterHTML("", "pp-slide__flat") + "</div>";
    thumbs += '<button type="button" class="pp-thumbnail' + (plainOn ? " on" : "") + '" data-slide="' + this.mockups.length + '" aria-label="Print only">' +
      this.posterHTML("", "") + "</button>";

    this.el.stage.innerHTML = slides;
    // Mockup thumbnails, so choosing a frame cycles the images as it always has.
    // Their saved designs belong on the configure step, not here.
    this.el.thumbs.innerHTML = this.mockups.length ? thumbs : "";
    // The markup above carries no colour or name of its own — posterHTML leaves
    // both to this, so there is exactly one place that decides them.
    this.applyPosterStyle();
    this.bindThumbs();
    this.syncNav();

    if (this.el.caption) {
      var m2 = this.mockups[this.activeMockup];
      this.el.caption.textContent = m2 ? (m2.label || "") : "Print only";
    }
  };

  // Arrows step through whatever the stage is currently showing: the product
  // gallery before generation, the frame mockups after.
  Studio.prototype.slideCount = function () {
    return this.artUrl ? this.mockups.length + 1 : this.gallery.length;
  };

  Studio.prototype.step = function (dir) {
    var total = this.slideCount();
    if (total < 2) return;
    this.activeMockup = (this.activeMockup + dir + total) % total;
    this.renderStage();
  };

  Studio.prototype.syncNav = function () {
    var many = this.slideCount() > 1;
    if (this.el.prev) this.el.prev.hidden = !many;
    if (this.el.next) this.el.next.hidden = !many;
  };

  Studio.prototype.bindThumbs = function () {
    var self = this;
    this.el.thumbs.querySelectorAll("[data-slide]").forEach(function (b) {
      b.addEventListener("click", function () {
        self.activeMockup = parseInt(b.dataset.slide, 10);
        self.renderStage();
      });
    });
  };

  // Called when the customer picks a frame on the right. Finds the mockup whose
  // frame value matches and moves the carousel to it.
  Studio.prototype.syncStageToFrame = function () {
    if (!this.artUrl) return;
    var want = norm(this.chosenFrame);
    var idx = -1;
    this.mockups.forEach(function (m, i) { if (norm(m.frame) === want) idx = i; });
    if (idx === -1 && (want === "print only" || this.chosenFormat === "digital")) {
      idx = this.mockups.length; // the plain slide
    }
    if (idx !== -1 && idx !== this.activeMockup) {
      this.activeMockup = idx;
      this.renderStage();
    }
  };

    // Before a portrait exists, choosing an option (The Happy Hour's drink) moves
  // the product photos to the one whose alt text names it. The link is the alt
  // text in Shopify, so adding a drink photo needs no code: set its alt text to
  // the drink exactly as the dropdown shows it. EXACT match, not "contains", or
  // "Classic martini" would also catch "Espresso martini".
  Studio.prototype.syncGalleryToOption = function (groupId, value) {
    if (this.artUrl) return;
    var group = null;
    (this.style.optionGroups || []).forEach(function (g) { if (g.id === groupId) group = g; });
    if (!group) return;
    var opt = null;
    group.options.forEach(function (o) { if (o.id === value) opt = o; });
    if (!opt) return;
    var wants = [norm(opt.label), norm(opt.id)];
    var idx = -1;
    this.gallery.forEach(function (g, i) {
      if (idx === -1 && wants.indexOf(norm(g.label)) !== -1) idx = i;
    });
    if (idx !== -1 && idx !== this.activeMockup) {
      this.activeMockup = idx;
      this.renderStage();
    }
  };

  /* ---- generation --------------------------------------------------------- */

  Studio.prototype.setCtaLabel = function (text) {
    var el = this.el.ctaLabel;
    if (!el || el.textContent === text) return;
    // Cross-fade rather than snap: text flicking inside a button is the single
    // thing that makes it read as a spinner instead of a status.
    el.classList.add("is-swapping");
    setTimeout(function () {
      el.textContent = text;
      el.classList.remove("is-swapping");
    }, 280);
  };

  Studio.prototype.startProgress = function () {
    var self = this;
    var started = Date.now();

    this.el.generate.disabled = true;
    if (this.el.spin) this.el.spin.hidden = false;

    this.progressTimer = setInterval(function () {
      var secs = (Date.now() - started) / 1000;
      // Eases towards 95% and only completes when the image actually lands, so
      // the fill never sits full while the customer waits.
      self.el.bar.style.width = Math.min(95, 100 * (1 - Math.exp(-secs / 16))) + "%";
      self.setCtaLabel(self.lines[Math.min(self.lines.length - 1, Math.floor(secs / 5))]);
    }, 250);
  };

  // ON A PHONE THE PORTRAIT LANDS OFF SCREEN. The button they pressed is
  // below the stage, so without this they finish generating looking at the
  // order options and have to scroll up to see what they made. Desktop has
  // both columns side by side, so it is left alone there.
  Studio.prototype.showResultOnPhone = function () {
    if (!window.matchMedia || !window.matchMedia("(max-width: 749px)").matches) return;
    var target = this.el.stage;
    if (!target) return;
    // Clear a sticky header, if the theme has one, so it doesn't sit over
    // the top of the portrait.
    var offset = 12;
    var header = document.querySelector(".shopify-section-group-header-group, header");
    if (header) {
      var pos = window.getComputedStyle(header).position;
      if (pos === "sticky" || pos === "fixed") offset += header.getBoundingClientRect().height;
    }
    var top = target.getBoundingClientRect().top + window.pageYOffset - offset;
    var still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: Math.max(0, top), behavior: still ? "auto" : "smooth" });
  };

  Studio.prototype.stopProgress = function (reveal) {
    var self = this;
    clearInterval(this.progressTimer);

    var settle = function () {
      if (self.el.spin) self.el.spin.hidden = true;
      self.el.bar.style.width = "0%";
      self.setCtaLabel(self.root.dataset.ctaLabel || "See my portrait");
      self.updateGenerateState();
    };

    if (!reveal) return settle();

    // Let the fill reach the end and land on a full stop before the reveal, so
    // the finish feels like an arrival rather than the bar vanishing.
    this.el.bar.style.width = "100%";
    this.setCtaLabel("Done.");
    setTimeout(function () {
      settle();
      self.el.stage.classList.add("is-revealing");
      setTimeout(function () { self.el.stage.classList.remove("is-revealing"); }, 700);
    }, 620);
  };

  // Test only, behind a section setting. Fakes everything the real flow returns
  // so the framing and checkout side can be worked on without paying Gemini
  // five pence a click. The placeholder is drawn locally, so it never touches
  // the Worker and can't be mistaken for a real portrait.
  Studio.prototype.skipGeneration = function () {
    // Test only. An inline SVG placeholder, so it needs no canvas and never
    // touches the Worker. Says so on its face to stop it being mistaken for a
    // real portrait in a screenshot.
    var svg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="768" height="1024">' +
      '<rect width="100%" height="100%" fill="#E7E3DC"/>' +
      '<circle cx="384" cy="560" r="210" fill="#B9AE9C"/>' +
      '<text x="384" y="200" text-anchor="middle" font-family="sans-serif" font-size="34" font-weight="600" fill="#6A635C">TEST IMAGE</text>' +
      '<text x="384" y="236" text-anchor="middle" font-family="sans-serif" font-size="20" fill="#6A635C">not a real portrait</text>' +
      "</svg>";

    this.artUrl = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
    this.spec = { artUrl: this.artUrl, width: 768, height: 1024 };
    this.handoff = null;
    this.testMode = true;
    this.activeMockup = 0;
    this.setPhase("result");
    this.renderChoices();
    this.renderStage();
    this.syncStageToFrame();
  };

  Studio.prototype.generate = function () {
    var self = this;
    if (!this.style) return;
    if (!this.validate()) return;

    this.showError(null);
    this.setPhase("configure");
    this.startProgress();

    // The bypass header only exists while the testing setting is on; in normal
    // operation there's no header and the quota applies.
    var headers = {};
    if (this.root.dataset.bypass) headers["x-pp-bypass"] = this.root.dataset.bypass;

    // A FRESH Turnstile token per generation. Tokens are single use and expire in
    // about five minutes, so one taken at page load fails for anyone who spends a
    // while choosing a photo. Resolves to "" if Turnstile is off or unavailable,
    // and never rejects, so this can't be what stops a generation.
    this.turnstileToken().then(function (token) {
      var fd = new FormData();
      fd.append("file", self.files[0]);
      // file2, file3… numbered rather than repeating the same key. A repeated
      // key works in FormData but reads like an accident at this end, and
      // numbering makes a missing middle photo obvious in the network tab. The
      // Worker reads them by name and stops at the style's own maxPhotos, so the
      // cap is enforced server-side rather than trusted from here.
      for (var i = 1; i < self.files.length; i++) {
        fd.append("file" + (i + 1), self.files[i]);
      }
      fd.append("selection", JSON.stringify(self.selection));
      if (token) fd.append("turnstileToken", token);

      return fetch(self.base + "/api/preview", { method: "POST", mode: "cors", headers: headers, body: fd })
      .then(function (r) {
        return r.json().then(function (body) {
          // 429 is the quota wall, and it deserves better than the red error
          // strip: it isn't a mistake the customer made.
          if (r.status === 429) {
            var err = new Error(body.error || "You've used your previews for today.");
            err.quota = true;
            throw err;
          }
          if (!r.ok) throw new Error(body.error || "Preview failed.");
          return body;
        });
      })
      .then(function (body) {
        self.spec = body.spec;
        self.handoff = body.previewHandoffToken;
        self.testMode = false;
        return self.paint(body.spec);
      })
      .then(function () {
        self.setPhase("result");
        self.renderChoices();
        self.syncStageToFrame();
        self.stopProgress(true);
        self.showResultOnPhone();
      })
      .catch(function (err) {
        self.stopProgress(false);
        if (err && err.quota && self.el.limitDialog) return self.showLimit(err.message);
        self.showError(err.message || "Something went wrong. Please try again.");
      });
    });
  };

  /* ---- compositing --------------------------------------------------------

     Only runs for spec.layer === "cutout". A finished poster still just gets
     displayed, exactly as before.
  ------------------------------------------------------------------------- */

  // Pull in the faces the compositor draws with, and resolve once they are
  // usable. Named sizes are irrelevant — any size loads the whole face — but the
  // family names must match the cssFamily values in the style's nameTreatments
  // and the @font-face rules in the section, or this silently loads nothing.
  function ready() {
    if (!document.fonts || !document.fonts.load) return Promise.resolve();
    return Promise.all([
      document.fonts.load('400 100px "Corinthia"'),
      document.fonts.load('700 100px "Roboto"')
    ]).catch(function () { /* draw with whatever is available */ });
  }

  // Where the animal lands, as percentages of the poster. These two numbers are
  // the whole point: whatever size and position the model produced, the poster
  // comes out the same.
  //
  // HEIGHT MUST EXCEED (100 - TOP). At top 21% and height 74% the body stops at
  // 95% and leaves a strip of background under it, which reads as a sticker
  // rather than a portrait. 80 runs the chest just off the bottom edge.
  var SUBJECT_TOP_PCT = 21;
  var SUBJECT_HEIGHT_PCT = 80;

  // NOTHING IS MEASURED HERE ANY MORE, and that is the point.
  //
  // This file used to scan the layer's alpha channel to find the animal, then
  // scale and place it. It failed three times in a row, always the same way: the
  // layer carries a watermark, a half-opacity mark that overlaps itself reads as
  // about three quarters opaque, and no threshold could tell that apart from
  // soft fur. One version measured the whole layer as animal and shrank the dog
  // to two thirds size.
  //
  // The Worker now asks Gemini where the animal is and sends the layer ALREADY
  // PLACED at poster size. The browser draws it 1:1. There is no measurement to
  // get wrong, and the watermark is exactly the size and coverage the server
  // drew, because nothing rescales it.

  // The name spec, rebuilt from the CURRENT selection rather than the one the
  // Worker saw. That is what lets the font, the ink and the text change without
  // regenerating.
  //
  // The precedence here must match lib/composite.ts and lib/print.ts: the
  // customer's chosen ink wins, then the treatment's, then auto. All three have
  // to agree or the printed name is a different colour from the approved one.
  Studio.prototype.nameSpecNow = function (bgHex) {
    var text = String(this.selection.displayName || "").trim();
    if (!text) return null;
    var treatments = this.style.nameTreatments || [];
    var t = null;
    for (var i = 0; i < treatments.length; i++) {
      if (treatments[i].fontId === this.selection.nameFontId) { t = treatments[i]; break; }
    }
    if (!t) t = treatments[0];
    if (!t) return null;

    var self = this;
    var chosen = null;
    (this.style.nameColors || []).forEach(function (c) {
      if (c.id === self.selection.nameColorId) chosen = c;
    });
    var ink = (chosen && chosen.hex) || (t.colorFrom === "auto" ? inkFor(bgHex) : t.colorFrom);

    return {
      text: text,
      cssFamily: t.cssFamily,
      weight: t.weight || 400,
      letterSpacing: t.letterSpacing || "0em",
      transform: t.transform || "none",
      ink: ink,
      topPct: t.topPct,
      fontSizePct: t.fontSizePct
    };
  };

  // Mirrors inkFor in lib/composite.ts. Duplicated deliberately: the alternative
  // is another round trip for a number the browser already has everything to
  // work out, and the formula has not changed since it was written.
  function inkFor(hex) {
    var h = String(hex || "").replace("#", "");
    if (h.length !== 6) return "#1C1A19";
    var r = parseInt(h.slice(0, 2), 16);
    var g = parseInt(h.slice(2, 4), 16);
    var b = parseInt(h.slice(4, 6), 16);
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 > 0.55 ? "#1C1A19" : "#F4F1EA";
  }

  /** The human label for the current swatch, e.g. "Sage". For the order. */
  Studio.prototype.bgLabelNow = function () {
    var self = this, label = "";
    (this.style.optionGroups || []).forEach(function (g) {
      if (g.kind !== "swatch") return;
      g.options.forEach(function (o) {
        if (o.id === self.selection.options[g.id]) label = o.label;
      });
    });
    return label;
  };

  /** The background hex for the current swatch selection. */
  Studio.prototype.bgHexNow = function () {
    var self = this, hex = null;
    (this.style.optionGroups || []).forEach(function (g) {
      if (g.kind !== "swatch") return;
      g.options.forEach(function (o) {
        if (o.id === self.selection.options[g.id]) hex = o.hex;
      });
    });
    return hex || (this.spec && this.spec.bgHex) || "#EFEBE4";
  };

  /* ---- the poster, as DOM rather than a bitmap -----------------------------

     THE POSTER IS BUILT OUT OF ELEMENTS, NOT DRAWN TO A CANVAS, and the reason
     is worth recording because the canvas version worked and still had to go.

     Canvas meant every swatch click re-encoded a 1152x1536 image to a base64
     data URL — megabytes of string — and then rebuilt the stage's innerHTML,
     which destroys and recreates the <img>. That is a visible pause followed by
     a flash of empty frame, on every single change. Measured against the
     competitor, whose recolour fires no network request at all and does not
     flash, that was clearly the wrong shape.

     Layered, a recolour is one CSS custom property. Nothing re-encodes, nothing
     is recreated, and the browser composites it on the GPU. Changing the name is
     a text node. There is nothing left to be slow.

     WHAT THIS MEANS FOR THE WATERMARK: the visible tiling is a CSS background,
     which anyone can remove from devtools — but the LAYER ITSELF still carries
     the mark burnt into its pixels by lib/cutout-layer.ts, so the only file that
     can actually be saved is already marked. The CSS pass is presentation; the
     protection sits underneath it and always did.
  ------------------------------------------------------------------------- */

  /** The poster as markup. `extra` styles the outer element for its slot. */
  Studio.prototype.posterHTML = function (extra, cls) {
    var spec = this.spec;
    if (!spec || spec.layer !== "cutout") {
      // A server-composited poster is already finished — one image, no layers.
      return '<img class="' + (cls || "") + '" src="' + this.artUrl + '" alt="Your portrait" style="' +
        (extra || "") + '">';
    }
    var own = this.hook("posterHTML", extra, cls);
    if (own != null) return own;
    // NO WATERMARK ELEMENT HERE, deliberately. The layer is rendered 1.6x the
    // poster's size with the mark across all of it, so once it is scaled to
    // place the animal it always overhangs the poster edges. One watermark, at
    // one size, covering everything — see lib/cutout-layer.ts.
    return '<span class="pp-poster ' + (cls || "") + '" style="' + (extra || "") + '">' +
      '<img class="pp-poster__art" src="' + spec.artUrl + '" alt="Your portrait">' +
      '<span class="pp-poster__name"></span>' +
    "</span>";
  };

  // Push the CURRENT colour and name onto every poster already on the page.
  // This is the whole live-update path: no rebuild, no re-encode, no reload.
  Studio.prototype.applyPosterStyle = function () {
    var spec = this.spec;
    if (!spec || spec.layer !== "cutout") return;
    if (this.hook("applyPosterStyle")) return;
    var bg = this.bgHexNow();
    var name = this.nameSpecNow(bg);
    var root = this.root || document;

    root.querySelectorAll(".pp-poster").forEach(function (el) {
      el.style.setProperty("--pp-bg", bg);
      var n = el.querySelector(".pp-poster__name");
      if (!n) return;
      if (!name) { n.textContent = ""; return; }
      n.textContent =
        name.transform === "uppercase" ? name.text.toUpperCase()
        : name.transform === "capitalise" ? name.text.charAt(0).toUpperCase() + name.text.slice(1)
        : name.text;
      n.style.color = name.ink;
      n.style.fontFamily = name.cssFamily;
      n.style.fontWeight = name.weight;
      n.style.letterSpacing = name.letterSpacing;
      // cqh: a percentage of the POSTER's height, whatever size the poster is
      // being displayed at. That is what keeps the name in proportion inside a
      // small mockup thumbnail and a full-width stage alike, and it mirrors
      // fontSizePct/topPct on the server exactly.
      n.style.fontSize = name.fontSizePct + "cqh";
      n.style.top = name.topPct + "cqh";
    });
  };

  /**
   * Apply an edit. No network, no re-encode, no DOM rebuild — this is a style
   * change on elements that are already on the page, which is why it is instant
   * and cannot flash.
   */
  Studio.prototype.recompose = function () {
    if (!this.spec || this.spec.layer !== "cutout") return false;
    // The stored bgHex has to follow the customer's edits, because it is what
    // the mockup tints, the email background and the PRINT all read. Leaving it
    // at the generate-time value would ship a poster in a colour they changed
    // away from.
    this.spec.bgHex = this.bgHexNow();
    // The stored poster and the handoff token were both written at generation
    // time and know nothing about this edit. Flagged here, settled in
    // finaliseDesign() before the design goes anywhere that matters.
    this.dirty = true;
    this.applyPosterStyle();
    return true;
  };

  // A finished poster is just displayed. A cutout layer is measured once and
  // then composited — and re-composited on every option change after that.
  Studio.prototype.paint = function (spec) {
    var self = this;
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.onload = function () {
        self.activeMockup = 0;
        if (spec.layer === "cutout") {
          // Fonts must be in before the first draw or the name renders in a
          // fallback face and then silently changes on the next recompose.
          //
          // document.fonts.ready IS NOT ENOUGH ON ITS OWN. A @font-face that no
          // DOM element uses is never fetched, and drawing to a canvas does not
          // trigger a fetch either — ready resolves immediately having loaded
          // nothing, the name is drawn in a fallback face, and it only comes
          // right when something else on the page happens to pull the font in.
          // Each face has to be asked for explicitly.
          Promise.all([ready(), self.hook("fontsReady")]).then(function () {
            // artUrl stays the LAYER's url. Everything that consumes it — the
            // hidden preview field, the cart line item — wants a real URL, and
            // the stage now builds elements rather than reading a bitmap.
            self.artUrl = spec.artUrl;
            self.renderStage();
            self.applyPosterStyle();
            resolve();
          });
          return;
        }
        self.artUrl = spec.artUrl;
        self.renderStage();
        resolve();
      };
      img.onerror = function () { reject(new Error("Couldn't load your portrait.")); };
      img.src = spec.artUrl;
    });
  };

  /* ---- format, size, frame ------------------------------------------------ */

  Studio.prototype.formatsAvailable = function () {
    var out = [];
    this.variants.forEach(function (v) { if (out.indexOf(v.format) === -1) out.push(v.format); });
    return out;
  };

  Studio.prototype.minPriceFor = function (format) {
    var min = null;
    this.variants.forEach(function (v) {
      if (v.format === format && (min === null || v.price < min)) min = v.price;
    });
    return min;
  };

  Studio.prototype.sizesFor = function (format) {
    var out = [];
    this.variants.forEach(function (v) {
      if (v.format === format && out.indexOf(v.size) === -1) out.push(v.size);
    });
    return out;
  };

  Studio.prototype.framesFor = function (format, size) {
    var out = [];
    this.variants.forEach(function (v) {
      if (v.format === format && v.size === size && out.indexOf(v.frame) === -1) out.push(v.frame);
    });
    return out;
  };

  Studio.prototype.currentVariant = function () {
    var self = this;
    var match = null;
    this.variants.forEach(function (v) {
      if (v.format === self.chosenFormat && v.size === self.chosenSize && v.frame === self.chosenFrame) match = v;
    });
    return match;
  };

  Studio.prototype.sizeLabelFor = function (raw) {
    // One unit only, the visitor's own. Both together is clutter; the size
    // guide is where the other one belongs.
    if (this.units === "cm") return inchesToCm(raw) || cleanInches(raw);
    return cleanInches(raw);
  };

  Studio.prototype.renderChoices = function () {
    var self = this;
    var isDigital = this.chosenFormat === "digital";

    var cards = "";
    if (this.digital) {
      cards += '<button type="button" class="pp-fmt' + (isDigital ? " on" : "") + '" data-format="digital">' +
        '<span class="pp-fmt__name">' + esc(this.digital.title) + "</span>" +
        '<span class="pp-fmt__price">' + this.money(this.digital.price) + "</span></button>";
    }
    this.formatsAvailable().forEach(function (f) {
      cards += '<button type="button" class="pp-fmt' + (self.chosenFormat === f ? " on" : "") +
        '" data-format="' + esc(f) + '">' +
        '<span class="pp-fmt__name">' + esc(f) + "</span>" +
        '<span class="pp-fmt__price">from ' + self.money(self.minPriceFor(f)) + "</span></button>";
    });
    // BUILD THE FORMAT CARDS ONCE, then only move the "on" class.
    //
    // This used to reassign innerHTML on every call, which destroyed and replaced
    // the very button that had just been clicked. That is why the switch to
    // Digital would not animate: the elements being measured were detached from
    // the document a frame later, so their heights read as zero and the movement
    // had nothing to interpolate. Rebuilding also threw away and re-attached every
    // click handler on every click.
    //
    // Prices only change when the variant data reloads, so the markup can be
    // written once and left alone.
    if (this.el.formats.dataset.built !== "1") {
      this.el.formats.innerHTML = cards;
      this.el.formats.dataset.built = "1";
      this.el.formats.querySelectorAll("[data-format]").forEach(function (b) {
        b.addEventListener("click", function () {
          // Size and frame are kept across a format change too, for the same
          // reason. Poster and canvas share most sizes and finishes, so someone
          // who picked 18x24 in dark wood should still have it after switching.
          // Anything the new format does not offer is corrected below.
          self.chosenFormat = b.dataset.format;
          self.renderChoices();
          self.syncStageToFrame();
        });
      });
    }
    // Selection state, every time.
    this.el.formats.querySelectorAll("[data-format]").forEach(function (b) {
      var mine = b.dataset.format === "digital" ? isDigital : self.chosenFormat === b.dataset.format;
      b.classList.toggle("on", mine);
    });

    if (!isDigital) {
      var sizes = this.sizesFor(this.chosenFormat);
      if (sizes.indexOf(this.chosenSize) === -1) {
        this.chosenSize = prefer(sizes, function (s) { return /18\s*[x×]\s*24/i.test(s); });
      }

      this.el.sizes.innerHTML = sizes.map(function (s) {
        return '<button type="button" class="pp-size' + (self.chosenSize === s ? " on" : "") +
          '" data-size="' + esc(s) + '">' + esc(self.sizeLabelFor(s)) +
          (/18\s*[x×]\s*24/i.test(s) ? '<span class="pp-size__tag">Most popular</span>' : "") +
          "</button>";
      }).join("");
      this.el.sizes.querySelectorAll("[data-size]").forEach(function (b) {
        b.addEventListener("click", function () {
          // THE FRAME IS KEPT. It used to be cleared here, so choosing dark wood
          // and then changing your mind about the size silently dropped you back
          // to Print only — the frame list falls back to its first entry when
          // nothing is chosen, and Print only is first.
          //
          // renderChoices already handles the case where the chosen frame is not
          // offered in the new size: it checks the list and falls back only then.
          // So keeping the value here costs nothing and preserves the choice
          // wherever it is still available, which is nearly always.
          self.chosenSize = b.dataset.size;
          self.renderChoices();
          self.syncStageToFrame();
        });
      });

      var frames = this.framesFor(this.chosenFormat, this.chosenSize);
      if (frames.indexOf(this.chosenFrame) === -1) {
        this.chosenFrame = prefer(frames, function (f) { return norm(f) === "black"; });
      }
      if (this.el.frameLabel) this.el.frameLabel.textContent = this.chosenFrame || "";

      this.el.frames.innerHTML = frames.map(function (f) {
        var sw = FRAME_SWATCHES[norm(f)] || { hex: "#DDDDDD" };
        return '<button type="button" class="pp-frame' + (self.chosenFrame === f ? " on" : "") +
          '" data-frame="' + esc(f) + '" aria-label="' + esc(f) + '">' +
          '<span class="pp-frame__dot' + (sw.outline ? " is-outline" : "") + (sw.sheen ? " is-sheen" : "") +
          '" style="background:' + sw.hex + '"></span>' +
          '<span class="pp-frame__label">' + esc(f) + "</span></button>";
      }).join("");
      this.el.frames.querySelectorAll("[data-frame]").forEach(function (b) {
        b.addEventListener("click", function () {
          self.chosenFrame = b.dataset.frame;
          self.renderChoices();
          self.syncStageToFrame();
        });
      });

      if (this.el.sizeLabel) this.el.sizeLabel.textContent = this.sizeLabelFor(this.chosenSize);
      if (this.el.sizeGuide) this.el.sizeGuide.hidden = !this.guideSrc("size");
      if (this.el.frameGuide) this.el.frameGuide.hidden = !this.guideSrc("frame");
    }

    // AFTER the fields are populated, never before: a hidden element has no
    // layout, so its scrollHeight is zero and the reveal would animate to nothing.
    // ONE animation for the whole change.
    //
    // Previously the two fields animated themselves and the summary animated
    // separately, so the movement stopped partway and then jumped. Now the fields
    // are hidden or shown instantly INSIDE a wrapper whose height is animated, and
    // the summary is rewritten in the same pass, so every part of the column moves
    // on one clock.
    var self2 = this;
    if (window.PP_DEBUG_ANIM) this.debugHeights("format -> " + this.chosenFormat);
    this.animateHeight(this.el.printOptions, function () {
      if (self2.el.sizeField) self2.el.sizeField.hidden = isDigital;
      if (self2.el.frameField) self2.el.frameField.hidden = isDigital;
      // Lets the CSS pull the summary up when there is nothing above it. Set
      // inside the mutate callback so it is part of the measured change and moves
      // with the animation rather than after it.
      if (self2.el.printOptions) {
        self2.el.printOptions.classList.toggle("pp-print-options--empty", isDigital);
      }
    });
    this.animateHeight(this.el.summary, function () {
      self2.renderSummary();
      self2.renderEta();
    });
  };

  /**
   * Run a function that changes an element's contents, and animate the height
   * change rather than letting it snap.
   *
   * Measure, mutate, measure, then animate between the two. One element, one
   * animation: the alternative is animating each row in and out separately, which
   * is what produced a slide that stopped partway and then jumped.
   */
  // ===========================================================================
  // TEMPORARY. Delete this method and the two calls to it once the animation is
  // settled.
  //
  // Watches every element in the control column for a second and a half and lists
  // whatever changes height, with timings, into a panel on the page. Anything
  // moving at or after 280ms is the jump: that is what needs animating, or what
  // needs to stop moving.
  // ===========================================================================
  Studio.prototype.debugHeights = function (label) {
    var col = this.el && this.el.summary ? this.el.summary.closest(".pp-ctrl-col") : null;
    if (!col) return;

    var panel = document.getElementById("pp-anim-debug");
    if (!panel) {
      panel = document.createElement("pre");
      panel.id = "pp-anim-debug";
      panel.style.cssText = "position:fixed;bottom:0;left:0;right:0;z-index:99999;" +
        "background:#111;color:#0f0;font:11px monospace;padding:8px;margin:0;" +
        "max-height:45vh;overflow:auto";
      document.body.appendChild(panel);
    }
    panel.textContent = label + "\n";

    var els = [].slice.call(col.querySelectorAll("div,ul,li,p,span"));
    var prev = els.map(function (e) { return e.getBoundingClientRect().height; });
    var t0 = performance.now();

    (function tick() {
      var t = Math.round(performance.now() - t0);
      els.forEach(function (e, i) {
        var h = e.getBoundingClientRect().height;
        if (Math.abs(h - prev[i]) > 1) {
          panel.textContent += t + "ms  " + Math.round(prev[i]) + "->" + Math.round(h) +
            "  " + e.tagName.toLowerCase() + "." +
            (String(e.className || "").split(" ")[0] || "-") + "\n";
          prev[i] = h;
        }
      });
      if (performance.now() - t0 < 1500) requestAnimationFrame(tick);
      else panel.textContent += "done\n";
    })();
  };

  Studio.prototype.animateHeight = function (el, mutate) {
    if (!el) { mutate(); return; }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      mutate();
      return;
    }

    var MS = 280;

    // overflow:hidden BEFORE measuring, and it stays on afterwards. This is the
    // whole bug, and it is worth understanding because it is invisible in the code.
    //
    // overflow:hidden creates a new block formatting context, and inside one, a
    // child's bottom margin no longer collapses out through the parent. So the
    // element measures TALLER with overflow hidden than without it — by 22px here,
    // the two option fields' 14px margins collapsed together.
    //
    // Setting overflow only for the duration of the animation meant measuring in
    // one mode and finishing in another: the height animated correctly to a value
    // that was 22px too tall, then the cleanup removed overflow, margins collapsed
    // again, and the column dropped the last 22px in a single frame. That is the
    // "slides then pops" the logs showed at 339ms, after the animation had ended
    // at 272ms.
    //
    // Measuring and finishing in the same mode removes the discrepancy entirely.
    el.style.overflow = "hidden";
    var before = el.getBoundingClientRect().height;
    mutate();
    el.style.height = "";
    var after = el.getBoundingClientRect().height;

    // Animating a one pixel change looks like a flicker rather than a movement.
    if (Math.abs(after - before) < 2) {
      el.style.height = "";
      return;
    }

    el.style.height = before + "px";
    requestAnimationFrame(function () {
      el.style.transition = "height " + MS + "ms cubic-bezier(.4,0,.2,1)";
      el.style.height = after + "px";
      window.setTimeout(function () {
        // Height back to auto so the element can still grow on its own. overflow
        // deliberately stays hidden: restoring it would collapse the margins again
        // and reintroduce the jump.
        el.style.transition = "";
        el.style.height = "";
      }, MS);
    });
  };

  Studio.prototype.summaryRow = function (label, value, muted) {
    return '<div class="pp-sum__row' + (muted ? " muted" : "") + '"><span>' + label + "</span><span>" + value + "</span></div>";
  };

  Studio.prototype.renderSummary = function () {
    var rows = "";
    var total = 0;
    var ok = false;

    if (this.chosenFormat === "digital" && this.digital) {
      total = this.digital.price;
      ok = true;
      rows += this.summaryRow(esc(this.digital.title), this.money(this.digital.price));
      rows += this.summaryRow("Delivery", "Instant, by email");
    } else {
      var v = this.currentVariant();
      if (v) {
        total = v.price;
        ok = true;
        // THE FRAME ON ITS OWN LINE, with its own price, so it is plain that the
        // frame is something they are paying for and what it costs. The print
        // line shows the Print only price for the same format and size.
        var framed = v.frame && norm(v.frame) !== "print only";
        var base = null;
        if (framed) {
          this.variants.forEach(function (x) {
            if (x.format === v.format && x.size === v.size && norm(x.frame) === "print only") base = x.price;
          });
        }
        if (framed && base != null && v.price > base) {
          rows += this.summaryRow(esc(v.format + ", " + this.sizeLabelFor(v.size)), this.money(base));
          rows += this.summaryRow(esc(v.frame + " frame"), this.money(v.price - base));
        } else {
          var frameBit = framed ? ", " + v.frame + " frame" : "";
          rows += this.summaryRow(esc(v.format + ", " + this.sizeLabelFor(v.size) + frameBit), this.money(v.price));
        }
        if (this.digital) {
          rows += this.summaryRow("Digital file", "<s>" + this.money(this.digital.price) + "</s> Included");
        }
        rows += this.summaryRow(
          esc(this.root.dataset.shipLabel || "Shipping"),
          esc(this.root.dataset.shipValue || "Free")
        );
      } else {
        rows += this.summaryRow("That combination isn't available", "", true);
      }
    }

    this.el.summary.innerHTML = rows +
      '<div class="pp-sum__total"><span>Total</span><span>' + (ok ? this.money(total) : "\u2014") + "</span></div>";
    this.setOrderButtons(ok);
  };

  /**
   * Enable or disable BOTH order buttons together.
   *
   * They were managed separately and drifted: addToCart disabled both, and the
   * success path re-enabled only `add`. That was invisible when the button led
   * straight to checkout — the page navigates away — but "Add to basket" then
   * "Create another" left Buy it now greyed out for the rest of the session, on a
   * design the customer had just made. renderSummary had the same gap, setting
   * `add` from the summary's validity and never touching `buy`.
   *
   * There is no state in which one should be usable and the other not, so they
   * are set together, always.
   */
  Studio.prototype.setOrderButtons = function (enabled) {
    if (this.el.add) this.el.add.disabled = !enabled;
    if (this.el.buy) this.el.buy.disabled = !enabled;
  };

  /* ---- cart --------------------------------------------------------------- */

  /**
   * Re-composite the stored poster and re-mint the token with what the customer
   * ACTUALLY chose, rather than what they chose before they started adjusting.
   *
   * WHY THIS IS NOT OPTIONAL. The colour, name, font and ink can all change
   * after generating — the browser redraws instantly and nothing is regenerated,
   * which is the point of the cut-out. But previews/ and the handoff token were
   * both written at generation time. Without this, a customer could generate on
   * green, switch to purple, order the purple one and receive a green one, with
   * a green picture in their confirmation email.
   *
   * No Gemini calls: the animal is already cut out and already measured. This is
   * a redraw.
   *
   * RESOLVES EVEN ON FAILURE. A design that ships in the generated colour is
   * wrong about the colour; a blocked Add to cart is a lost sale.
   */
  Studio.prototype.finaliseDesign = function () {
    var self = this;
    if (!this.dirty || !this.handoff || !this.spec || this.spec.layer !== "cutout") {
      return Promise.resolve();
    }
    return fetch(this.base + "/api/finalise", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ handoffToken: this.handoff, selection: this.selection })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (out) {
        if (!out || !out.handoffToken) return;
        self.handoff = out.handoffToken;
        // THE FLAT POSTER, NOT artUrl. Overwriting artUrl here replaced the
        // transparent layer the compositor draws from with a finished poster, so
        // any later redraw painted a whole poster inside the poster — background,
        // name and all. They mean different things and only one of them changed.
        self.spec.posterUrl = out.previewUrl;
        self.dirty = false;
      })
      .catch(function () { /* ship what we have */ });
  };

  /**
   * The picture to hand to ANYTHING OUTSIDE THE STUDIO — the cart, the checkout,
   * the order confirmation, the save-for-later email.
   *
   * NOT spec.artUrl. On the cut-out path that is the transparent animal, which
   * is exactly right for the compositor here and quietly wrong everywhere else:
   * a transparent PNG in a cart thumbnail or an email renders on white, so a
   * customer who picked a deep green background saw their pet on nothing.
   *
   * posterUrl is the flat poster with their colour and their name burnt in. On
   * the older server-composited path the two are the same file, so this is safe
   * either way.
   */
  Studio.prototype.posterSrc = function () {
    return (this.spec && (this.spec.posterUrl || this.spec.artUrl)) || this.artUrl;
  };

  Studio.prototype.addToCart = function (goToCheckout) {
    var self = this;
    if (this.testMode) {
      return this.showError("This is the test image, so it can't be ordered. Generate a real portrait first.");
    }
    if (!this.handoff) return this.showError("Generate your portrait first.");
    if (this.hook("validate") === false) return;

    var isDigital = this.chosenFormat === "digital";
    var variant = isDigital ? null : this.currentVariant();
    if (!isDigital && !variant) return this.showError("That combination isn't available.");

    this.setOrderButtons(false);
    this.finaliseDesign().then(function () { self.sendToCart(goToCheckout, isDigital, variant); });
  };

  /** The cart call itself, once the design is settled. */
  Studio.prototype.sendToCart = function (goToCheckout, isDigital, variant) {
    var self = this;
    // VISIBLE PROPERTIES ARE THE ORDER'S RECEIPT. Anything without a leading
    // underscore shows on the confirmation email, the packing slip and the admin
    // line item — so these are what the customer checks against what they
    // approved, and what you check before sending a file to print. The colour
    // matters most: it is the one thing they can change after generating, so it
    // is the one thing most worth stating back to them in writing.
    var nameStyle = "";
    (this.style.nameTreatments || []).forEach(function (t) {
      if (t.fontId === self.selection.nameFontId) nameStyle = t.label;
    });

    var props = {
      "Style": this.style.name,
      "Name": this.selection.displayName || "None",
      "Background color": this.bgLabelNow() || "—",
      "_style_id": this.style.id,
      "_selection": JSON.stringify(this.selection),
      "_preview_url": this.posterSrc(),
      "_handoff_token": this.handoff
    };
    // Only when there is a name — an order that says 'Name style: Modern' beside
    // 'Name: None' reads like something went wrong.
    if (this.selection.displayName && nameStyle) props["Name style"] = nameStyle;
    if (!isDigital) props["_digital_included"] = this.digital ? "yes" : "no";
    this.hook("cartProps", props);

    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: [{ id: isDigital ? this.digital.id : variant.id, quantity: 1, properties: props }]
      })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (b) { throw new Error(b.description || "Couldn't add to cart."); });
        // Re-enabled even when heading to checkout: the redirect usually makes it
        // moot, but a slow navigation or a customer hitting back should not find
        // dead buttons.
        self.setOrderButtons(true);
        if (goToCheckout) {
          window.location.href = "/checkout";
          return;
        }
        self.showAddedToCart(props, isDigital);
      })
      .catch(function (err) {
        self.setOrderButtons(true);
        self.showError(err.message);
      });
  };

  /* ---- boot --------------------------------------------------------------- */

  function boot() {
    document.querySelectorAll("[data-pp-studio]").forEach(function (root) {
      if (root.dataset.ppBooted) return;
      root.dataset.ppBooted = "1";
      new Studio(root);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
  document.addEventListener("shopify:section:load", boot);
})();